import React, { forwardRef, useRef, useState, createRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import * as Yup from 'yup';
import i18n from 'i18n-js';
import { Grid, TextField as MuiTextField, Box, Typography } from '@mui/material';
import { BaseModal } from 'components/UI';
import { Formik, Form, Field } from 'formik';
import { AuthActions } from 'state/modules/auth';
import { PhoneField, TextField } from 'components/FormFields';
import { useDispatch, useSelector } from 'react-redux';

const ChangePhoneModal = ({ open, user, handleClick }) => {
  const [phone, setPhone] = useState('123');
  const [refs, setRefs] = useState([]);

  const formSchema = Yup.object().shape({
    current_phone: Yup.string().nullable(),
    mobile_phone: Yup.string().nullable().required(i18n.t('common.required_field')),
  });

  const codeSchema = Yup.object().shape({
    code1: Yup.number().required(),
    code2: Yup.number().required(),
    code3: Yup.number().required(),
    code4: Yup.number().required(),
    code5: Yup.number().required(),
    code6: Yup.number().required(),
  });

  const formRef = useRef(null);
  const codeFormRef = useRef(null);
  const dispatch = useDispatch();
  const { fetchingUpdateAccount, fetchedUpdateAccount } = useSelector((state) => state?.auth || {});

  const onSubmit = () => {
    const form = fetchedUpdateAccount ? codeFormRef : formRef;
    form.current.submitForm();
  };

  useEffect(() => {
    setRefs([...Array(6)].map(() => createRef()));
  }, []);

  const InputRef = forwardRef((props, ref) => {
    return (
      <Field name={props.name} type="number">
        {({ field, form }) => (
          <MuiTextField
            data-cy={props.name}
            error={props.error ? true : false}
            inputRef={ref}
            type="number"
            sx={{
              margin: '0 5px',
              '& input': {
                textAlign: 'center',
                '&::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                },
                '&::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                },
              },
            }}
            {...field}
            onChange={(e) => {
              e.preventDefault();
              if (e.target.value.length > 1) {
                return;
              }
              form.setFieldValue(props.name, e.target.value);
              if (props.index && e.target.value && !isNaN(e.target.value)) {
                refs[props.index].current.focus();
              }
            }}
          />
        )}
      </Field>
    );
  });

  InputRef.displayName = 'InputRef';

  return (
    <BaseModal
      size="sm"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={onSubmit}
      onCancel={handleClick}
      enableCloseButton={false}
      handleClick={handleClick}
      loading={fetchingUpdateAccount}
      title={i18n.t('account.change_phone')}
      enterButtonLabel={
        fetchedUpdateAccount ? i18n.t('account.verify') : i18n.t('account.change_phone')
      }
    >
      {!fetchedUpdateAccount ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            current_phone: user?.mobile_phone || '',
            mobile_phone: user?.mobile_phone || '',
          }}
          onSubmit={(values, { setErrors }) => {
            const { mobile_phone, phone_code } = values;
            if (mobile_phone === `+${phone_code}`) {
              return setErrors({ mobile_phone: i18n.t('common.required_field') });
            }
            setPhone(mobile_phone);
            dispatch(
              AuthActions.fetchUpdateAccount({ mobile_phone }, 'phone', null, {
                type: 'info',
                message: 'account.change_phone_success',
              })
            );
          }}
          validationSchema={formSchema}
        >
          {({ handleSubmit }) => (
            <Form noValidate onSubmit={handleSubmit} autoComplete="off">
              <Grid data-cy="change-phone-modal" container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Field
                    disabled
                    margin="none"
                    name="current_phone"
                    data-cy="current_phone"
                    datacy="current_phone"
                    component={user?.mobile_phone ? PhoneField : TextField}
                    label={i18n.t('account.current_phone')}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Field
                    margin="none"
                    name="mobile_phone"
                    data-cy="new_phone"
                    datacy="new_phone"
                    dialcodename="phone_code"
                    component={user?.mobile_phone ? PhoneField : TextField}
                    label={i18n.t('account.new_phone')}
                  />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      ) : (
        <Formik
          innerRef={codeFormRef}
          initialValues={{
            code1: '',
            code2: '',
            code3: '',
            code4: '',
            code5: '',
            code6: '',
          }}
          validationSchema={codeSchema}
          onSubmit={(values) => {
            const code = `${values.code1}${values.code2}${values.code3}${values.code4}${values.code5}${values.code6}`;
            dispatch(
              AuthActions.fetchUpdateAccount(
                { params: code },
                'phone',
                handleClick,
                { type: 'success', message: 'account.verify_phone_success' },
                'code'
              )
            );
          }}
        >
          {({ touched, errors, handleSubmit, ...props }) => (
            <Form noValidate onSubmit={handleSubmit} autoComplete="off" data-cy="code-form">
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography color="textSecondary">
                    <Box fontWeight="fontWeightMedium" component="span" fontSize={15}>
                      {i18n.t('account.change_phone_msg_1')} {phone}.{' '}
                      {i18n.t('account.change_phone_msg_2')}
                    </Box>
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box
                    mt={1}
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    {refs.map((val, index) => {
                      const name = `code${index + 1}`;
                      return (
                        <InputRef
                          key={index}
                          index={index !== refs.length - 1 ? index + 1 : null}
                          error={errors[name] && touched[name]}
                          name={name}
                          ref={refs[index]}
                          {...props}
                        />
                      );
                    })}
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      )}
    </BaseModal>
  );
};

ChangePhoneModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
  user: PropTypes.shape({
    mobile_phone: PropTypes.string,
  }).isRequired,
  name: PropTypes.string,
  error: PropTypes.string,
  index: PropTypes.number,
};

ChangePhoneModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default ChangePhoneModal;
